import { ViewModel } from "mvvm/ViewModel";
import { JobViewModel } from "./JobViewModel";
import { PageViewModel } from "./PageViewModel";

export class QuestionViewModel extends ViewModel {

    private _sqlQueries: string[] = [''];
    private _sizes: number[] = [0];
    private _selectedJob: JobViewModel | undefined = undefined;
    private _jobUnsubscribe: (() => void) | undefined = undefined;

    constructor(public readonly page: PageViewModel) {
        super();
    }

    get sqlQueries() {
        return this._sqlQueries;
    }

    set sqlQueries(value: string[]) {
        if (this._sqlQueries !== value) {
            this._sqlQueries = value;
            this.updateViews();
        }
    }

    get sizes() {
        return this._sizes;
    }

    set sizes(value: number[]) {
        if (this._sizes !== value) {
            this._sizes = value;
            this.updateViews();
        }
    }

    get selectedJob() {
        return this._selectedJob;
    }

    set selectedJob(value: JobViewModel | undefined) {
        if (this._selectedJob !== value) {
            // Unsubscribe from previous job's updates
            if (this._jobUnsubscribe) {
                this._jobUnsubscribe();
                this._jobUnsubscribe = undefined;
            }

            this._selectedJob = value;

            // Subscribe to new job's updates
            if (value) {
                const callback = () => {
                    this.updateViews();
                };
                value.addView(callback);
                this._jobUnsubscribe = () => value.removeView(callback);
            }

            this.updateViews();
        }
    }

    get submitButtonCaption() {
        return this.isSubmitting ? "Submitting" : "Submit";
    }

    // With the new grid-based SQL input, the view layer is responsible for
    // deciding when the configuration is complete. Here we only gate on
    // submission state; the SQL array passed into submit is validated there.
    get canSubmit() {
        return !this.isSubmitting;
    }

    get hasInput() {
        return this._sqlQueries.some(q => q?.trim());
    }

    async submit(sqlQueries: string[], sizes: number[], alias?: string, dimensions?: number) {
        if (this.isSubmitting || !sqlQueries.some(q => q?.trim())) {
            return;
        }
            this.isSubmitting = true;
        const jobInfo = await this.page.jobService.createJob(this.page.modelAlias, sqlQueries, sizes, alias, undefined, dimensions);
            this.page.addJob(jobInfo);
            this.clearContent();
            this.isSubmitting = false;
    }

    async regenerate(jobID: string, sqlQueries: string[], sizes: number[], alias?: string, dimensions?: number) {
        if (this.isSubmitting || !sqlQueries.some(q => q?.trim())) {
            return;
        }
        this.isSubmitting = true;
        try {
            const jobInfo = await this.page.jobService.regenerateJobWithParams(jobID, sqlQueries, sizes, alias, dimensions);
            this.page.addJob(jobInfo);

            // Reload jobs to get the new job
            await this.page.reloadJobs();

            // Select the new job
            const newJob = this.page.jobs.find(j => j.jobID === jobInfo.jobID);
            if (newJob) {
                this.page.selectedJob = newJob;
            }

            this.clearContent();
        } catch (error) {
            console.error("Failed to regenerate dashboard:", error);
            throw error;
        } finally {
            this.isSubmitting = false;
        }
    }

    get isSubmitting() {
        return this.page.isSubmitting;
    }

    private set isSubmitting(value: boolean) {
        if (this.page.isSubmitting !== value) {
            this.page.isSubmitting = value;
            this.updateViews();
        }
    }

    get canClear() {
        return !this.isSubmitting && (this.hasInput || this.selectedJob !== undefined);
    }

    clearContent() {
        this._sqlQueries = [''];
        this._sizes = [0];
        this.updateViews();
    }

    reset() {
        this._sqlQueries = [''];
        this._sizes = [0];
        this.page.selectedJob = undefined;
        this.updateViews();
    }

    dispose() {
        if (this._jobUnsubscribe) {
            this._jobUnsubscribe();
            this._jobUnsubscribe = undefined;
        }
    }
}